import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const CLEVIA_GREEN = '16c47f';
const HEADER_BG = CLEVIA_GREEN;
const HEADER_FONT_COLOR = 'FFFFFF';
const ALT_ROW_BG = 'F0FDF4';
const BORDER_COLOR = 'E5E7EB';
const TOTAL_BG = '111827';
const TOTAL_FONT_COLOR = CLEVIA_GREEN;

interface ExportColumn {
  header: string;
  key: string;
  width?: number;
  style?: 'number' | 'currency' | 'date' | 'text';
}

interface ExportOptions {
  sheetName: string;
  fileName: string;
  columns: ExportColumn[];
  data: Record<string, unknown>[];
  totals?: Record<string, number>;
  currency?: string;
}

const thinBorder: Partial<ExcelJS.Border> = { style: 'thin', color: { argb: BORDER_COLOR } };
const allBorders: Partial<ExcelJS.Borders> = {
  top: thinBorder,
  bottom: thinBorder,
  left: thinBorder,
  right: thinBorder,
};

export async function exportStyledExcel({
  sheetName,
  fileName,
  columns,
  data,
  totals,
  currency = '€',
}: ExportOptions) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'clevia';
  wb.created = new Date();

  const ws = wb.addWorksheet(sheetName);

  // ── Define columns ──
  ws.columns = columns.map((col) => ({
    header: col.header,
    key: col.key,
    width: col.width || 18,
  }));

  // ── Style header row ──
  const headerRow = ws.getRow(1);
  headerRow.height = 32;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: HEADER_FONT_COLOR }, size: 11, name: 'Calibri' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.border = allBorders;
  });

  // ── Add data rows ──
  data.forEach((row) => {
    ws.addRow(row);
  });

  // ── Style data rows ──
  for (let i = 2; i <= data.length + 1; i++) {
    const row = ws.getRow(i);
    row.height = 26;
    const isAlt = i % 2 === 0;

    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.font = { size: 10.5, name: 'Calibri', color: { argb: '374151' } };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.border = allBorders;

      if (isAlt) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_BG } };
      }

      // Apply column-specific formatting
      const colDef = columns[colNumber - 1];
      if (colDef?.style === 'currency' && typeof cell.value === 'number') {
        cell.numFmt = `#,##0.00 "${currency}"`;
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
        cell.font = { ...cell.font, bold: true };
      } else if (colDef?.style === 'number' && typeof cell.value === 'number') {
        cell.numFmt = '#,##0.00';
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
      } else if (colDef?.style === 'date') {
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      }
    });
  }

  // ── Totals row ──
  if (totals) {
    ws.addRow({});
    const totalRowNum = data.length + 2;
    const tRow = ws.getRow(totalRowNum);
    tRow.height = 32;

    columns.forEach((col, idx) => {
      const cell = tRow.getCell(idx + 1);
      if (idx === 0) {
        cell.value = 'TOTAL';
      } else if (totals[col.key] !== undefined) {
        cell.value = totals[col.key];
        cell.numFmt = `#,##0.00 "${currency}"`;
      }
      cell.font = { bold: true, color: { argb: TOTAL_FONT_COLOR }, size: 11, name: 'Calibri' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TOTAL_BG } };
      cell.alignment = { vertical: 'middle', horizontal: idx === 0 ? 'left' : 'right' };
      cell.border = allBorders;
    });
  }

  // ── Auto‑filter ──
  ws.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: columns.length },
  };

  // ── Freeze header row ──
  ws.views = [{ state: 'frozen', ySplit: 1 }];

  // ── Export ──
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, fileName);
}
